import {useEffect, useRef, useState} from 'react'
import {type PDFDocumentProxy} from 'pdfjs-dist'

import {renderPdfPage} from '#/lib/document/pdf.web'
import {PDF_SKELETON_CLASS} from '#/lib/document/pdfStyles.web'
import {useNonReactiveCallback} from '#/lib/hooks/useNonReactiveCallback'
import {logger} from '#/logger'

/**
 * Renders one page of a document into a canvas.
 *
 * Rendering happens at `width * dpr` and is blitted down to the display size,
 * so pages stay crisp on retina screens. When the page changes, the previous
 * render stays on screen until the new one is painted — no flash of blank
 * canvas — and renders that are no longer needed still land in the shared
 * cache, which makes going back a page instant.
 */
export function PdfPageCanvas({
  doc,
  docKey,
  pageNumber,
  width,
  height,
  dpr,
  onPainted,
}: {
  doc: PDFDocumentProxy
  docKey: string
  pageNumber: number
  /** Display size in CSS pixels */
  width: number
  height: number
  dpr: number
  onPainted?: () => void
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const hasPaintedRef = useRef(false)
  const [isPristine, setIsPristine] = useState(true)

  /*
   * What to say once the page is on screen, read through a ref.
   *
   * This is the one prop here that reaches an *effect's* dependencies, which makes a
   * caller that writes it as an arrow the caller's own render a re-render of the
   * page: the effect is torn down (cancelling the paint in flight) and run again, so
   * every re-render of the card or the reader would repaint the same page. The
   * wrapper is stable, so a fresh arrow from the caller is not news — and the paint
   * that is running still says the same thing when it finishes
   * (`useNonReactiveCallback`). The reader and the card both take no callback today;
   * this is what keeps the day somebody passes one from being a repaint per render.
   */
  const painted = useNonReactiveCallback(onPainted)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || width <= 0 || height <= 0) return

    let cancelled = false

    ;(async () => {
      try {
        const rendered = await renderPdfPage({
          doc,
          docKey,
          pageNumber,
          targetWidth: width,
          dpr,
        })
        if (cancelled) return

        const context = canvas.getContext('2d', {alpha: false})
        if (!context) return

        const targetWidth = Math.max(1, Math.round(width * dpr))
        const targetHeight = Math.max(1, Math.round(height * dpr))
        if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
          canvas.width = targetWidth
          canvas.height = targetHeight
        }
        context.fillStyle = '#ffffff'
        context.fillRect(0, 0, targetWidth, targetHeight)
        context.drawImage(rendered.canvas, 0, 0, targetWidth, targetHeight)

        if (!hasPaintedRef.current) {
          hasPaintedRef.current = true
          setIsPristine(false)
        }
        painted()
      } catch (e) {
        logger.debug('Failed to paint pdf page', {
          docKey,
          pageNumber,
          safeMessage: e,
        })
      }
    })()

    return () => {
      cancelled = true
    }
  }, [doc, docKey, dpr, height, pageNumber, painted, width])

  return (
    <div
      style={{
        position: 'relative',
        width,
        height,
        backgroundColor: '#ffffff',
        transition: 'width 180ms ease-out, height 180ms ease-out',
      }}>
      <canvas
        ref={canvasRef}
        style={{width: '100%', height: '100%', display: 'block'}}
        aria-hidden="true"
      />
      {isPristine && <div className={PDF_SKELETON_CLASS} />}
    </div>
  )
}
